/**
 * Format date to ISO 8601 format (YYYY-MM-DD) or time (HH:MM:SS) or both
 * @param {string|Date} date - Date to format
 * @param {string} type - Format type: 'date' (YYYY-MM-DD), 'time' (HH:MM:SS), or 'datetime' (YYYY-MM-DD HH:MM:SS)
 * @returns {string} Formatted date/time string
 */
export function formatDate(date, type = 'datetime') {
  if (!date) return '';

  const d = new Date(date);
  if (isNaN(d.getTime())) return '';

  // Pad numbers with leading zero
  const pad = (n) => String(n).padStart(2, '0');

  const year = d.getFullYear();
  const month = pad(d.getMonth() + 1);
  const day = pad(d.getDate());
  const hours = pad(d.getHours());
  const minutes = pad(d.getMinutes());
  const seconds = pad(d.getSeconds());

  switch (type) {
    case 'date':
      return `${year}-${month}-${day}`;
    case 'time':
      return `${hours}:${minutes}:${seconds}`;
    case 'datetime':
    default:
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }
}

/**
 * Format duration in seconds to MM:SS
 */
export function formatDuration(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Truncate text to max length
 */
export function truncate(text, maxLength = 50) {
  if (!text || text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + '...';
}

/**
 * Normalize game name for display.
 * - Trims whitespace
 * - Title-cases words while preserving short all-caps acronyms (e.g. PUBG, CS)
 * - Preserves segments separated by ':' and re-joins with ': '
 */
export function formatGameName(name) {
  if (!name) return '';
  const s = String(name).trim();

  const titleCaseWord = (word) => {
    if (!word) return word;
    // Preserve short acronyms (<= 4 chars) that are already uppercase
    if (word === word.toUpperCase() && word.length <= 4) return word;
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  };

  return s
    .split(':')
    .map(segment => segment.trim()
      .split(/\s+/)
      .map(titleCaseWord)
      .join(' ')
    )
    .join(': ');
}

/**
 * Format number with thousands separator or K/M abbreviation
 * @param {number} num - Number to format
 * @param {boolean} abbreviate - Whether to use K/M abbreviations
 * @returns {string} Formatted number
 */
export function formatNumber(num, abbreviate = false) {
  if (!num || num === 0) return '0';
  
  if (abbreviate) {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
  }
  
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}
